import ReactDOM from 'react-dom/client'
import { reactErrorHandler } from "@sentry/react";
import initSentry from "./sentry";
import './index.css'
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Search } from "./pages/Search";
import { config } from "./config"
import Keycloak from "keycloak-js";
import { NavigationDefinition } from "@hostingbv/lib/src/components/layout/AuthenticatedLayout";
import { AuthMiddleware, AuthProvider } from "@hostingbv/lib/src/auth/AuthProvider";
import { AppMetadataResolver } from '@hostingbv/lib/src/components/layout/AppMetadataResolver';
import colors from "tailwindcss/colors";
import { generateDynamicColorSchemeCSS } from '@hostingbv/lib/src/util/colorScheme';
import { Domains } from "./pages/Domains";
import { Customers } from "./pages/Customers";
import { DomainSearch } from "./pages/DomainSearch";
import { DomainOrder } from "./pages/DomainOrder";
import { DomainDetails } from "./pages/DomainDetails";
import { CustomerDetails } from "./pages/CustomerDetails";
import countries from "i18n-iso-countries";
import * as countriesJson from "i18n-iso-countries/langs/en.json"
import { DomainDnsEditor } from './pages/DomainDnsEditor';
import { Servers } from "./pages/Servers";
import { Sites } from "./pages/Sites";
import { SshKeys } from "./pages/SshKeys";
import { SiteCreate } from "./pages/SiteCreate";
import { SiteDetails } from "./pages/SiteDetails";
import { SshKeyDetails } from "./pages/SshKeyDetails";
import { RealtimeDeploymentProviderPartnerAdapter } from "./components/RealtimeDeploymentProviderPartnerAdapter";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { NotFound } from "@hostingbv/lib/src/components/content/NotFound";
import { ServerDetails } from "./pages/ServerDetails";
import { RealtimeDeploymentStatusBar } from "./components/deployments/RealtimeDeploymentStatusBar";
import { HttpClient, useApiClient } from "../../lib/src/api";
import { DomainDnsImport } from "./pages/DomainDnsImport";
import { DomainPrices } from "./pages/DomainPrices";
import { MailDomains } from "./pages/MailDomains";
import { MailDomainDetails } from "./pages/MailDomainDetails";
import { Settings } from "./pages/Settings";
import { DeploymentLog } from "./pages/DeploymentLog";
import { faClockRotateLeft, faEnvelopeOpenText, faFileShield, faGear, faGlobe, faKey, faMagnifyingGlass, faPeopleGroup, faServer, faShop } from '@fortawesome/free-solid-svg-icons';
import { QueryClient } from '@hostingbv/lib/src/api/state';
import { Certificates } from './pages/Certificates';
import { CertificateCreate } from './pages/CertificateCreate';
import { CertificateDetails } from './pages/CertificateDetails';
import { CertificateFinalize } from './pages/CertificateFinalize';
import { QueryProvider } from '@hostingbv/lib/src/api/QueryProvider';

countries.registerLocale(countriesJson);

const navigation: NavigationDefinition = [
  {
    link: '/',
    text: 'Search',
    icon: faMagnifyingGlass,
  },
  { header: 'Organization' },
  {
    link: '/customers',
    text: 'Customers',
    icon: faPeopleGroup,
    permissionConstraints: {
      resource: 'customers',
      requiredPermission: 'view',
    }
  },
  {
    link: '/log',
    text: 'History',
    icon: faClockRotateLeft,
    permissionConstraints: {
      resource: 'deployments',
      requiredPermission: 'view'
    }
  },
  { header: 'Resources' },
  {
    link: '/domains',
    text: 'Domains',
    icon: faGlobe,
    permissionConstraints: {
      resource: 'domains',
      requiredPermission: 'view',
    }
  },
  {
    link: '/certificates',
    text: 'Certificates',
    icon: faFileShield,
    permissionConstraints: {
      resource: 'certificates',
      requiredPermission: 'view',
    }
  },
  {
    link: '/sites',
    text: 'Sites',
    icon: faShop,
    permissionConstraints: {
      resource: 'sites',
      requiredPermission: 'view',
    }
  },
  {
    link: '/servers',
    text: 'Servers',
    icon: faServer,
    permissionConstraints: {
      resource: 'servers',
      requiredPermission: 'view',
    }
  },
  {
    link: '/mail',
    text: 'E-mail',
    beta: true,
    icon: faEnvelopeOpenText,
    permissionConstraints: {
      resource: 'mail',
      requiredPermission: 'view',
    }
  },
  {
    link: '/ssh-keys',
    text: 'SSH Keys',
    icon: faKey,
    permissionConstraints: {
      resource: 'authorizedKeys',
      requiredPermission: 'view',
    }
  },
  { header: 'Profile' },
  {
    link: '/settings',
    text: 'Settings',
    icon: faGear,
  },
]

initSentry()

ReactDOM.createRoot(document.getElementById('root') as HTMLElement, {
  onUncaughtError: reactErrorHandler(),
  onCaughtError: reactErrorHandler(),
  onRecoverableError: reactErrorHandler(),
}).render(
  <AppMetadataResolver metadataUrl={`${config.partnerApiUrl}metadata`} builder={(metadata) => {
    const authClient = new Keycloak(metadata.keycloakConfig)

    const queryClient = new QueryClient(new HttpClient(config.partnerApiUrl, async () => {
      await authClient.updateToken(60)
      return authClient.token!
    }))

    return <>
      {/* @ts-expect-error metadata.colorPalette is a narrower string union than tailwindcss's colors keys */}
      <style>{generateDynamicColorSchemeCSS(typeof metadata.colorPalette === "string" ? (colors[metadata.colorPalette] ?? colors.blue) : metadata.colorPalette)}</style>
      <AuthProvider authClient={authClient}>
        <QueryProvider client={queryClient}>
          <RealtimeDeploymentProviderPartnerAdapter>
            <BrowserRouter>
              <AuthMiddleware topChildren={<RealtimeDeploymentStatusBar />} config={config} client={'partnerClient'} navigation={navigation} allowedRoles={"any-capability"} appTitle={metadata.brandTitle} appSubTitle={metadata.brandSubTitle ?? ""} image={metadata.brandImageURL}>
                <RouteProvider />
              </AuthMiddleware>
            </BrowserRouter>
          </RealtimeDeploymentProviderPartnerAdapter>
        </QueryProvider>
      </AuthProvider>
    </>
  }} />
)

// eslint-disable-next-line react-refresh/only-export-components -- entry file, not subject to Fast Refresh
function RouteProvider() {
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)
  return <Routes>
    <Route path={"/"} element={<Search />} />
    {/* Customers */}
    {can('view', 'customers') && <Route path={"/customers"} element={<Customers />} />}
    {can('view', 'customers') && can('view', 'customerContacts') && <Route path={"/customers/:uuid"} element={<CustomerDetails />} />}
    {can('view', 'deployments') && <Route path={"/log"} element={<DeploymentLog />} />}
    {/* Domains */}
    {can('view', 'domains') && <Route path={"/domains"} element={<Domains />} />}
    {can('view', 'domains') && <Route path={"/domains/:uuid"} element={<DomainDetails />} />}
    {can('view', 'dns') && <Route path={"/domains/:uuid/dns"} element={<DomainDnsEditor />} />}
    {can('edit', 'dns') && <Route path={"/domains/:uuid/dns/import"} element={<DomainDnsImport />} />}
    {can('create', 'domains') && <Route path={"/domains/register"} element={<DomainSearch />} />}
    {can('create', 'domains') && <Route path={"/domains/register/:search"} element={<DomainSearch />} />}
    {can('create', 'domains') && <Route path={"/domains/order/:domain/:orderType"} element={<DomainOrder />} />}
    {can('view', 'contracts') && <Route path={"/domains/prices"} element={<DomainPrices />} />}
    {/* Certificates */}
    {can('view', 'certificates') && <Route path={"/certificates"} element={<Certificates />} />}
    {can('create', 'certificates') && <Route path={"/certificates/add"} element={<CertificateCreate />} />}
    {can('view', 'certificates') && <Route path={"/certificates/:certificate"} element={<CertificateDetails />} />}
    {can('edit', 'certificates') && <Route path={"/certificates/:certificate/finalize"} element={<CertificateFinalize />} />}
    {/* Servers */}
    {can('view', 'servers') && <Route path={"/servers"} element={<Servers />} />}
    {can('view', 'servers') && <Route path={"/servers/:server"} element={<ServerDetails />} />}
    {/* Sites */}
    {can('view', 'sites') && <Route path={"/sites"} element={<Sites />} />}
    {can('view', 'sites') && <Route path={"/sites/:site"} element={<SiteDetails />} />}
    {can('create', 'sites') && <Route path={"/sites/deploy"} element={<SiteCreate />} />}
    {/* Mail */}
    {can('view', 'mail') && <Route path={"/mail"} element={<MailDomains />} />}
    {can('view', 'mail') && <Route path={"/mail/:mailDomain"} element={<MailDomainDetails />} />}
    {/* SSH Keys */}
    {can('view', 'authorizedKeys') && <Route path={"/ssh-keys"} element={<SshKeys />} />}
    {can('view', 'authorizedKeys') && <Route path={"/ssh-keys/:sshKey"} element={<SshKeyDetails />} />}
    {/* Settings */}
    <Route path={"/settings"} element={<Settings />} />
    <Route path={"*"} element={<NotFound />} />
  </Routes>
}
